import React, { useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const EMPTY = { dealer_name: "", city: "", province: "", region: "", postal_code: "", address: "", telephone: "", email: "", opening_hours: "", dealer_description: "", latitude: 0, longitude: 0, test_drive_available: true, active: true };

export default function DealersAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: dealers = [], isLoading } = useQuery({ queryKey: ["admin-dealers"], queryFn: () => api.get("/dealers").then((r) => r.data) });

  const create = useMutation({ mutationFn: (b) => api.post("/admin/dealers", b).then((r) => r.data), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-dealers"] }); toast.success("Rivenditore creato"); setEditing(null); }, onError: () => toast.error("Errore") });
  const update = useMutation({ mutationFn: ({ id, b }) => api.patch(`/admin/dealers/${id}`, b).then((r) => r.data), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-dealers"] }); toast.success("Aggiornato"); setEditing(null); }, onError: () => toast.error("Errore") });

  const save = () => {
    const b = { ...editing, latitude: parseFloat(editing.latitude) || 0, longitude: parseFloat(editing.longitude) || 0 };
    if (editing.id) update.mutate({ id: editing.id, b }); else create.mutate(b);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Rivenditori</h1><p className="text-slate-500">{dealers.length} centri</p></div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="add-dealer">+ Aggiungi</Button>
      </div>

      {isLoading ? <p className="text-slate-500">Caricamento...</p> : (
        <div className="grid md:grid-cols-2 gap-4">
          {dealers.map((d) => (
            <div key={d.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`admin-dealer-${d.slug}`}>
              <div className="flex justify-between items-start">
                <div><h3 className="font-bold text-slate-900">{d.dealer_name}</h3><p className="text-slate-500 text-sm">{d.city} ({d.province}) · {d.telephone}</p></div>
                <Button size="sm" variant="outline" onClick={() => setEditing(d)} data-testid={`edit-dealer-${d.slug}`}>Modifica</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle>{editing.id ? "Modifica" : "Nuovo"} rivenditore</DialogTitle><DialogDescription>Compila i dati del centro rivenditore SENIORCAR.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                {[["dealer_name", "Nome centro"], ["address", "Indirizzo"], ["city", "Città"], ["province", "Provincia"], ["region", "Regione"], ["postal_code", "CAP"], ["telephone", "Telefono"], ["email", "Email"], ["opening_hours", "Orari"], ["latitude", "Latitudine"], ["longitude", "Longitudine"]].map(([k, l]) => (
                  <div key={k}><label className="text-sm font-semibold text-slate-700">{l}</label><Input value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="mt-1" data-testid={`dealer-${k}`} /></div>
                ))}
                <div><label className="text-sm font-semibold text-slate-700">Descrizione</label><Textarea value={editing.dealer_description ?? ""} onChange={(e) => setEditing({ ...editing, dealer_description: e.target.value })} rows={2} className="mt-1" /></div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /> Attivo</label>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={!!editing.test_drive_available} onCheckedChange={(v) => setEditing({ ...editing, test_drive_available: v })} /> Prova disponibile</label>
                </div>
                <Button onClick={save} className="w-full h-11 font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="dealer-save">Salva</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
